import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import {
  Users, UserPlus, Share2, FileText, Trophy, Loader2,
  Heart, MessageCircle, Link2, Search, ArrowLeft, Send,
  ChevronDown, ChevronUp, Star, Download, Trash2, Check, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

async function socialGet(path) {
  return api.get(`/social${path}`);
}

async function socialPost(path, data) {
  return api.post(`/social${path}`, data);
}

async function socialDelete(path) {
  return api.delete(`/social${path}`);
}

// ---- Feed Tab ----
function FeedTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState({});
  const [expandedPost, setExpandedPost] = useState(null);

  useEffect(() => { loadFeed(); }, []);
  const loadFeed = async () => {
    setLoading(true);
    try { const data = await socialGet('/feed'); setPosts(data.posts || []); }
    catch (e) { /* */ } finally { setLoading(false); }
  };

  const handleLike = async (shareId) => {
    try {
      const result = await socialPost(`/workout/${shareId}/like`);
      setPosts(prev => prev.map(p => p.share_id === shareId ? { ...p, likes: result.action === 'liked' ? [...(p.likes || []), 'me'] : (p.likes || []).slice(0, -1) } : p));
    } catch (e) { toast.error('Failed to like'); }
  };

  const handleComment = async (shareId) => {
    const text = commentText[shareId]?.trim();
    if (!text) return;
    try {
      const comment = await socialPost(`/workout/${shareId}/comment`, { text });
      setPosts(prev => prev.map(p => p.share_id === shareId ? { ...p, comments: [...(p.comments || []), comment] } : p));
      setCommentText(prev => ({ ...prev, [shareId]: '' }));
    } catch (e) { toast.error('Failed to comment'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-green-400" /></div>;

  return (
    <div className="space-y-3">
      {posts.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No posts yet. Add friends and share workouts!</p>
        </div>
      ) : posts.map(post => (
        <Card key={post.share_id} className="bg-slate-800/60 border-slate-700 rounded-xl">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div><span className="text-white font-medium text-sm">{post.user_name}</span>
                <span className="text-slate-500 text-xs ml-2">{new Date(post.created_at).toLocaleDateString()}</span></div>
            </div>
            {post.message && <p className="text-slate-300 text-sm">{post.message}</p>}
            {post.workout_data && (
              <div className="bg-slate-700/50 rounded-lg p-2 text-xs text-slate-400">
                {post.workout_data.name || 'Workout'} - {post.workout_data.exercises?.length || 0} exercises
              </div>
            )}
            <div className="flex items-center gap-4">
              <button onClick={() => handleLike(post.share_id)} className="flex items-center gap-1 text-slate-400 hover:text-red-400 transition-colors">
                <Heart className={`h-4 w-4 ${post.likes?.length > 0 ? 'fill-red-400 text-red-400' : ''}`} />
                <span className="text-xs">{post.likes?.length || 0}</span>
              </button>
              <button onClick={() => setExpandedPost(expandedPost === post.share_id ? null : post.share_id)} className="flex items-center gap-1 text-slate-400 hover:text-blue-400">
                <MessageCircle className="h-4 w-4" /><span className="text-xs">{post.comments?.length || 0}</span>
              </button>
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}?workout=${post.share_id}`); toast.success('Link copied!'); }}
                className="flex items-center gap-1 text-slate-400 hover:text-green-400">
                <Link2 className="h-4 w-4" />
              </button>
            </div>
            {expandedPost === post.share_id && (
              <div className="space-y-2 pt-2 border-t border-slate-700">
                {post.comments?.map((c, i) => (
                  <div key={i} className="text-xs"><span className="text-blue-400 font-medium">{c.user_name}: </span><span className="text-slate-300">{c.text}</span></div>
                ))}
                <div className="flex gap-2">
                  <Input value={commentText[post.share_id] || ''} onChange={e => setCommentText(prev => ({ ...prev, [post.share_id]: e.target.value }))}
                    placeholder="Write a comment..." className="bg-slate-700 border-slate-600 text-white text-xs h-8" onKeyDown={e => e.key === 'Enter' && handleComment(post.share_id)} />
                  <Button size="icon" onClick={() => handleComment(post.share_id)} className="h-8 w-8 bg-green-600 hover:bg-green-500"><Send className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---- Friends Tab ----
function FriendsTab() {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => { loadFriends(); loadRequests(); }, []);

  const loadFriends = async () => { try { const data = await socialGet('/friends'); setFriends(data.friends || []); } catch (e) { /* */ } };
  const loadRequests = async () => { try { const data = await socialGet('/friends/requests'); setRequests(data); } catch (e) { /* */ } };

  const searchUsers = useCallback(async () => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try { const data = await socialPost('/friends/search', { query: searchQuery }); setSearchResults(data.users || []); }
    catch (e) { /* */ } finally { setSearching(false); }
  }, [searchQuery]);

  useEffect(() => { const t = setTimeout(searchUsers, 500); return () => clearTimeout(t); }, [searchQuery, searchUsers]);

  const sendRequest = async (toUserId) => {
    try { await socialPost('/friends/request', { to_user_id: toUserId }); toast.success('Friend request sent!'); loadRequests(); }
    catch (e) { toast.error(e.message || 'Failed to send request'); }
  };

  const acceptRequest = async (requestId) => {
    try { await socialPost(`/friends/accept/${requestId}`); toast.success('Friend added!'); loadFriends(); loadRequests(); }
    catch (e) { toast.error('Failed to accept'); }
  };

  const rejectRequest = async (requestId) => {
    try { await socialPost(`/friends/reject/${requestId}`); loadRequests(); }
    catch (e) { /* */ }
  };

  const removeFriend = async (friendId) => {
    if (!window.confirm('Remove this friend?')) return;
    try { await socialDelete(`/friends/${friendId}`); loadFriends(); toast.success('Friend removed'); }
    catch (e) { toast.error('Failed to remove'); }
  };

  // Check if a user is already a friend or has a pending request
  const isFriendOrPending = (userId) => {
    if (friends.some(f => f.friend_user_id === userId)) return 'friend';
    if (requests.outgoing?.some(r => r.to_user_id === userId)) return 'pending';
    if (requests.incoming?.some(r => r.from_user_id === userId)) return 'incoming';
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search users by name or email..."
          className="bg-slate-800 border-slate-700 text-white pl-10 text-sm" />
      </div>
      {searchResults.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-slate-400">Search Results</p>
          {searchResults.map(u => {
            const status = isFriendOrPending(u.user_id);
            return (
              <div key={u.user_id} className="flex items-center justify-between bg-slate-800/60 rounded-xl px-3 py-2">
                <div><span className="text-white text-sm">{u.name}</span><span className="text-slate-500 text-xs ml-2">{u.email}</span></div>
                {status === 'friend' ? (
                  <span className="text-green-400 text-xs font-medium px-2">Friends</span>
                ) : status === 'pending' ? (
                  <span className="text-amber-400 text-xs font-medium px-2">Pending</span>
                ) : status === 'incoming' ? (
                  <span className="text-blue-400 text-xs font-medium px-2">Respond</span>
                ) : (
                  <Button size="sm" onClick={() => sendRequest(u.user_id)} className="bg-green-600 hover:bg-green-500 h-7 text-xs" data-testid="add-friend-btn"><UserPlus className="h-3.5 w-3.5 mr-1" />Add</Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {requests.incoming?.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-amber-400 font-medium">Pending Requests ({requests.incoming.length})</p>
          {requests.incoming.map(r => (
            <div key={r.request_id} className="flex items-center justify-between bg-slate-800/60 rounded-xl px-3 py-2">
              <span className="text-white text-sm">{r.from_user_name}</span>
              <div className="flex gap-1">
                <Button size="icon" onClick={() => acceptRequest(r.request_id)} className="h-7 w-7 bg-green-600"><Check className="h-3.5 w-3.5" /></Button>
                <Button size="icon" onClick={() => rejectRequest(r.request_id)} variant="outline" className="h-7 w-7 border-red-500/50 text-red-400"><X className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-1">
        <p className="text-xs text-slate-400">Friends ({friends.length})</p>
        {friends.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">No friends yet. Search and add some!</p>
        ) : friends.map(f => (
          <div key={f.friend_user_id} className="flex items-center justify-between bg-slate-800/60 rounded-xl px-3 py-2">
            <span className="text-white text-sm">{f.friend_name || f.friend_user_id}</span>
            <button onClick={() => removeFriend(f.friend_user_id)} className="text-red-400/70 hover:text-red-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Share Workout Tab ----
function ShareWorkoutTab() {
  const [message, setMessage] = useState('');
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    setSharing(true);
    try {
      await socialPost('/share-workout', { workout_data: { name: 'Today\'s Workout', exercises: [] }, message });
      toast.success('Workout shared!');
      setMessage('');
    } catch (e) { toast.error('Failed to share'); }
    finally { setSharing(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-800/60 border-slate-700 rounded-xl">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-white font-medium text-sm">Share a Workout</h3>
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Add a message about your workout..."
            className="w-full bg-slate-700 text-white text-sm rounded-xl p-3 border border-slate-600 outline-none focus:border-green-500 min-h-[80px] resize-none" />
          <Button onClick={handleShare} disabled={sharing} className="w-full bg-green-600 hover:bg-green-500">
            {sharing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Share2 className="h-4 w-4 mr-2" />}Share Workout
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ---- Templates Tab ----
function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (difficulty) params.set('difficulty', difficulty);
    try { const data = await socialGet(`/templates?${params}`); setTemplates(data.templates || []); }
    catch (e) { /* */ } finally { setLoading(false); }
  }, [search, difficulty]);

  useEffect(() => { const t = setTimeout(loadTemplates, 300); return () => clearTimeout(t); }, [loadTemplates]);

  const downloadTemplate = async (templateId) => {
    try { await socialPost(`/template/${templateId}/download`); toast.success('Template downloaded!'); }
    catch (e) { toast.error('Failed to download'); }
  };

  const rateTemplate = async (templateId, rating) => {
    try { await socialPost(`/template/${templateId}/rate`, { rating }); toast.success('Rating saved!'); loadTemplates(); }
    catch (e) { toast.error('Failed to rate'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..."
            className="bg-slate-800 border-slate-700 text-white pl-10 text-sm" />
        </div>
        <select value={difficulty} onChange={e => setDifficulty(e.target.value)}
          className="bg-slate-800 text-slate-300 text-sm rounded-md px-2 border border-slate-700 outline-none">
          <option value="">All</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-green-400" /></div>
      ) : templates.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-12">No templates found. Be the first to share one!</p>
      ) : templates.map(t => (
        <Card key={t.template_id} className="bg-slate-800/60 border-slate-700 rounded-xl">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === t.template_id ? null : t.template_id)}>
              <div>
                <h4 className="text-white text-sm font-semibold">{t.template_name}</h4>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-400" />{t.ratings?.length > 0 ? (t.ratings.reduce((a, r) => a + r.rating, 0) / t.ratings.length).toFixed(1) : '--'}</span>
                  <span><Download className="h-3 w-3 inline mr-0.5" />{t.downloads}</span>
                  <span className="capitalize">{t.difficulty}</span>
                </div>
              </div>
              {expandedId === t.template_id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </div>
            {expandedId === t.template_id && (
              <div className="space-y-2 pt-2 border-t border-slate-700">
                {t.description && <p className="text-slate-400 text-xs">{t.description}</p>}
                <p className="text-slate-500 text-xs">By {t.user_name} - {t.training_days} days/week</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => downloadTemplate(t.template_id)} className="bg-green-600 hover:bg-green-500 h-7 text-xs"><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(r => (
                      <button key={r} onClick={() => rateTemplate(t.template_id, r)} className="text-amber-400 hover:text-amber-300">
                        <Star className={`h-4 w-4 ${t.ratings?.some(rt => rt.rating >= r) ? 'fill-amber-400' : ''}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---- Leaderboard Tab ----
function LeaderboardTab() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => { loadLeaderboard(); }, [period]);
  const loadLeaderboard = async () => {
    setLoading(true);
    try { const data = await socialGet(`/leaderboard?period=${period}`); setLeaderboard(data.leaderboard || []); }
    catch (e) { /* */ } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1 bg-slate-800/50 rounded-lg p-1">
        {['week', 'month'].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors capitalize ${period === p ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'}`}>{p}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>
      ) : leaderboard.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-12">No rankings yet. Share workouts to compete!</p>
      ) : leaderboard.map((entry, i) => (
        <div key={i} className={`flex items-center gap-3 bg-slate-800/60 rounded-xl px-4 py-3 ${i === 0 ? 'border border-amber-500/30' : ''}`}>
          <span className={`text-lg font-bold w-8 ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-amber-700' : 'text-slate-500'}`}>#{entry.rank}</span>
          <div className="flex-1"><span className="text-white text-sm font-medium">{entry.user_name}</span></div>
          <span className="text-green-400 font-bold text-sm">{entry.score} workouts</span>
        </div>
      ))}
    </div>
  );
}

// ---- Main Community Component ----
export default function SocialFeed({ onBack, onNavigate, isExpired }) {
  const [activeTab, setActiveTab] = useState('feed');

  const tabs = [
    { id: 'feed', label: 'Feed', icon: Users },
    { id: 'friends', label: 'Friends', icon: UserPlus },
    { id: 'share', label: 'Share', icon: Share2 },
    { id: 'templates', label: 'Templates', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="community-page">
      <header className="sticky top-0 z-40 bg-slate-950 backdrop-blur-sm border-none ios-header" style={{ border: 'none' }}>
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
              <Users className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-white">Community</h1>
          </div>
          <button onClick={() => setActiveTab('leaderboard')} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${activeTab === 'leaderboard' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-amber-400'}`}>
            <Trophy className="h-4 w-4" /> Leaderboard
          </button>
        </div>
        <div className="max-w-lg mx-auto flex gap-1 px-4 pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                activeTab === t.id ? 'bg-green-500/20 text-green-400' : 'text-slate-400 hover:text-white'
              }`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 pb-32 max-w-lg">
        {activeTab === 'feed' && <FeedTab />}
        {activeTab === 'friends' && <FriendsTab />}
        {activeTab === 'share' && <ShareWorkoutTab />}
        {activeTab === 'templates' && <TemplatesTab />}
        {activeTab === 'leaderboard' && <LeaderboardTab />}
      </main>
    </div>
  );
}
